import { DatePipe } from '@angular/common';
import {
  ConfigStatut,
  ConseilConfigSortInput,
  InvestisseurType,
  Maybe,
  SortEnumType,
} from '@lib/models/generated/graphql';
import { AdminConfig, FicheConfigType } from 'projects/fiche-conseil-backoffice/src/app/services/admin-config.service';

export const MAJHeader = 'Mise à jour';

export abstract class FicheConfigColumn {
  public header: string;
  public withSort: boolean = false;
  public isRightAligned: boolean = false;
  public hideHeader = false;

  constructor(header: string, withSort: boolean = false, isRightAligned: boolean = false, hideHeader = false) {
    this.withSort = withSort;
    this.header = header;
    this.isRightAligned = isRightAligned;
    this.hideHeader = hideHeader;
  }
  public abstract getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined;
  public getSortCriteria(isAsc: boolean): ConseilConfigSortInput | undefined {
    return undefined;
  }
}

export class EnveloppeColumn extends FicheConfigColumn {
  constructor() {
    super('Enveloppe', true);
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config?.conditionsOfApplicability?.enveloppe?.libelle ?? '-';
  }

  public override getSortCriteria(isAsc: boolean): ConseilConfigSortInput | undefined {
    return { conditionsOfApplicability: { enveloppe: { libelle: isAsc ? SortEnumType.Asc : SortEnumType.Desc } } };
  }
}

export class TypeColumn extends FicheConfigColumn {
  constructor() {
    super('Type', true);
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    switch (config?.type) {
      case FicheConfigType.Digitale:
        return 'Digitale';
      case FicheConfigType.AvecDoc:
        return 'Avec doc.';
      case FicheConfigType.SansDoc:
        return 'Sans doc.';
      default:
        return '-';
    }
  }
}
export class StatutColumn extends FicheConfigColumn {
  constructor() {
    super('Statut', true);
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    switch (config?.statut) {
      case ConfigStatut.Draft:
        return 'draft';
      case ConfigStatut.Active:
        return 'active';
      default:
        return 'archived';
    }
  }
}

export class NatureDashboardColumn extends FicheConfigColumn {
  constructor() {
    super('Nature');
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    const libelle = config?.conditionsOfApplicability?.natureOperation?.libelle ?? '-';
    return libelle;
  }
}

export class LibelleColumn extends FicheConfigColumn {
  constructor() {
    super('Libelle');
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    const libelle =
      config?.libelle ??
      config?.conseilConfig?.libelle ??
      config?.conditionsOfApplicability?.natureOperation?.libelle ??
      '';
    return libelle;
  }
}

export class IsMajeurColumn extends FicheConfigColumn {
  constructor() {
    super('Majeur');
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config?.conseilConfig?.isMajeurVersion ? 'true' : 'false';
  }
}

export class ConfigVersionColumn extends FicheConfigColumn {
  constructor() {
    super('Config', false, true);
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config?.conseilConfig?.configFormulaire?.version?.toString()
      ? 'v' + config?.conseilConfig?.configFormulaire?.version?.toString()
      : '';
  }
}
export class MaJColumn extends FicheConfigColumn {
  constructor() {
    super('Mise à jour', true);
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return datePipe?.transform(config?.conseilConfig?.lastChangeDate, 'dd/MM/yyyy') ?? '';
  }
}

export class VersionColumn extends FicheConfigColumn {
  constructor() {
    super('Version');
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config?.conseilConfig?.version?.toString() ?? '';
  }
}

export class TemplateNameColumn extends FicheConfigColumn {
  constructor() {
    super('Nom du fichier template');
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config?.conseilConfig?.configTemplate?.templateName ?? '';
  }
}
export class DLCColumn extends FicheConfigColumn {
  constructor() {
    super('DLC');
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    // because of 2 in 1 design of the column, we need to check  the config status

    // case it's draft, we return the 'previousVersionDeprecationDate'
    if (config?.statut === ConfigStatut.Draft) {
      return datePipe?.transform(config?.conseilConfig?.previousVersionDeprecationDate, 'dd/MM/yyyy') ?? '';
    }
    // other cases we return the 'validityLimitDate'
    return datePipe?.transform(config?.conseilConfig?.validityLimitDate, 'dd/MM/yyyy') ?? '';
  }
}
export class CommentaireDeVersionColumn extends FicheConfigColumn {
  constructor() {
    super('Commentaire de version', false, false, true);
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config?.conseilConfig?.commentaireDeVersion ?? '';
  }
}

export class SetActiveColumn extends FicheConfigColumn {
  constructor() {
    super('Activer', false, false, true);
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config.statut === ConfigStatut.Draft ? config.conseilConfig?.id : undefined;
  }
}

export class DeleteDraftColumn extends FicheConfigColumn {
  constructor() {
    super('delete', false, false, true);
  }
  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config.statut === ConfigStatut.Draft ? config.conseilConfig?.id : undefined;
  }
}
export class DownloadTemplateColumn extends FicheConfigColumn {
  constructor() {
    super('Download template', false, false, true);
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config.conseilConfig?.configTemplate?.templateName ?? undefined;
  }
}
export class HabilitationColumn extends FicheConfigColumn {
  constructor() {
    super('Habilitation');
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config?.conditionsOfApplicability?.habilitation?.libelle ?? '-';
  }
}
export class PersonneColumn extends FicheConfigColumn {
  constructor() {
    super('Personne');
  }

  public override getDataFromFicheConfig(config: AdminConfig, datePipe?: DatePipe): Maybe<string> | undefined {
    return config.conditionsOfApplicability.typeInvestisseur === InvestisseurType.PersonneMorale
      ? 'Personne morale'
      : 'Personne physique';
  }
}
